import { useCallback, useEffect, useRef, useState } from 'react'

import {
  AbortError,
  allReady,
  readyIds,
  uploadFile,
  type Chip,
} from '../lib/attachments'
import { i18next } from '../i18n'
import { useSession } from '../session/SessionProvider'

/**
 * Dosya alimi: secici + yapistirma + surukleme, TEK yerde.
 *
 * Uc yolu birlestirmek onemli: ayri ayri yazilirsa uc farkli dogrulama, uc
 * farkli hata gosterimi ve uc farkli yukleme cagrisi olusuyor - ve biri
 * mutlaka geride kaliyor.
 *
 * ## `dragDepth` sayaci
 *
 * `dragenter`/`dragleave` olaylari IC OGELER uzerinde de tetikleniyor: fare
 * bir alt elemana girince parent'ta `dragleave` gorunuyor ve overlay
 * TITRIYOR. Sayac bunu cozuyor: yalnizca sifira dusunce kapaniyor. Vanilla
 * arayuzden AYNEN tasindi ve orada gorunur bir sorunun duzeltmesiydi.
 */
export interface FileIntake {
  chips: Chip[]
  /** Surukleme overlay'i gorunsun mu. */
  dragging: boolean
  /** Dosya ekle (secici/yapistirma/surukleme hepsi buraya geliyor). */
  add(files: Iterable<File>): void
  remove(localId: string): void
  clear(): void
  retry(localId: string): void
  /** Gonderilebilir mi (tum chip'ler hazir). */
  ready: boolean
  /** Sunucuya gonderilecek id'ler. */
  ids: string[]
}

let counter = 0
function nextLocalId(): string {
  counter += 1
  return `chip-${counter}`
}

export function useFileIntake(): FileIntake {
  const { state } = useSession()
  const csrf = state?.csrf_token ?? null
  const [chips, setChips] = useState<Chip[]>([])
  const [dragging, setDragging] = useState(false)
  const dragDepth = useRef(0)

  const patch = useCallback((localId: string, changes: Partial<Chip>) => {
    setChips((current) =>
      current.map((chip) => (chip.localId === localId ? { ...chip, ...changes } : chip)),
    )
  }, [])

  const start = useCallback(
    (chip: Chip) => {
      const handle = uploadFile(chip.file, csrf, {
        onProgress: (progress) => patch(chip.localId, { state: 'uploading', progress }),
        onProcessing: () => patch(chip.localId, { state: 'processing', progress: 100 }),
      })
      patch(chip.localId, { abort: handle.abort })

      handle.promise
        .then((uploaded) => patch(chip.localId, { state: 'ready', uploaded, error: undefined }))
        .catch((cause: unknown) => {
          // Kullanici iptali HATA DEGIL: chip zaten listeden kalkiyor.
          if (cause instanceof AbortError) return
          patch(chip.localId, {
            state: 'error',
            error: cause instanceof Error ? cause.message : i18next.t('attach.error.uploadFailed'),
          })
        })
    },
    [csrf, patch],
  )

  const add = useCallback(
    (files: Iterable<File>) => {
      const fresh: Chip[] = []
      for (const file of files) {
        // Bos dosya: sunucu da reddediyor ama kullaniciya bir chip gosterip
        // sonra hata vermek yerine hic almiyoruz.
        if (file.size === 0) continue
        fresh.push({ localId: nextLocalId(), file, state: 'queued', progress: 0 })
      }
      if (fresh.length === 0) return
      setChips((current) => [...current, ...fresh])
      // Yukleme HEMEN basliyor: gonderme aninda degil.
      for (const chip of fresh) start(chip)
    },
    [start],
  )

  const remove = useCallback((localId: string) => {
    setChips((current) => {
      const chip = current.find((item) => item.localId === localId)
      // Surmekte olan yuklemeyi iptal et: aksi halde kullanici chip'i
      // kaldirdiktan sonra kotasi yine harcanirdi.
      chip?.abort?.()
      return current.filter((item) => item.localId !== localId)
    })
  }, [])

  const clear = useCallback(() => {
    setChips((current) => {
      for (const chip of current) chip.abort?.()
      return []
    })
  }, [])

  const retry = useCallback(
    (localId: string) => {
      setChips((current) => {
        const chip = current.find((item) => item.localId === localId)
        if (chip) {
          // Yeniden deneme: durumu sifirla ve yukelemeyi tekrar baslat.
          start({ ...chip, state: 'queued', progress: 0, error: undefined })
        }
        return current.map((item) =>
          item.localId === localId
            ? { ...item, state: 'queued' as const, progress: 0, error: undefined }
            : item,
        )
      })
    },
    [start],
  )

  // Yapistirma: pano gorseli veya dosyasi.
  //
  // `document` seviyesinde dinliyoruz cunku kullanici composer'a odaklanmis
  // olmayabilir (ornegin mesaj listesine tikladiktan sonra yapistiriyor).
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.files
      if (!items || items.length === 0) return
      // Yalnizca DOSYA varsa mudahale ediyoruz: duz metin yapistirmasini
      // engellemek composer'i kullanilamaz hale getirirdi.
      event.preventDefault()
      add(Array.from(items))
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
  }, [add])

  // Sayfadan cikilirken surmekte olan yuklemeleri birak.
  useEffect(() => {
    return () => {
      for (const chip of chips) chip.abort?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnizca unmount
  }, [])

  // Surukleme: PENCERE seviyesinde dinliyoruz, tek bir elemana degil.
  //
  // Onceki hal `dropProps`u composer formuna spread ediyordu, yani dosya
  // YALNIZCA o kucuk kutunun uzerine birakilabiliyordu. Kullanicinin
  // sekmenin HERHANGI bir yerine surukleyip birakabilmesi gerekiyor - tipki
  // yapistirmanin `document` seviyesinde dinlenmesiyle AYNI gerekce
  // (yukarida). `window` secildi: `document`den farkli olarak `dragleave`
  // sayfa disina (baska bir pencereye/monitore) cikildiginda da guvenilir
  // tetikleniyor.
  useEffect(() => {
    function onDragEnter(event: DragEvent) {
      event.preventDefault()
      dragDepth.current += 1
      setDragging(true)
    }
    function onDragOver(event: DragEvent) {
      // `preventDefault` SART: olmadan tarayici dosyayi kendisi aciyor ve
      // uygulamadan cikiliyor.
      event.preventDefault()
    }
    function onDragLeave(event: DragEvent) {
      event.preventDefault()
      dragDepth.current -= 1
      // Yalnizca SIFIRA dusunce kapaniyor: ic ogeler uzerinde tetiklenen
      // `dragleave` overlay'i titretirdi.
      if (dragDepth.current <= 0) {
        dragDepth.current = 0
        setDragging(false)
      }
    }
    function onDrop(event: DragEvent) {
      event.preventDefault()
      dragDepth.current = 0
      setDragging(false)
      const files = event.dataTransfer?.files
      if (files && files.length > 0) add(Array.from(files))
    }

    window.addEventListener('dragenter', onDragEnter)
    window.addEventListener('dragover', onDragOver)
    window.addEventListener('dragleave', onDragLeave)
    window.addEventListener('drop', onDrop)
    return () => {
      window.removeEventListener('dragenter', onDragEnter)
      window.removeEventListener('dragover', onDragOver)
      window.removeEventListener('dragleave', onDragLeave)
      window.removeEventListener('drop', onDrop)
    }
  }, [add])

  return {
    chips,
    dragging,
    add,
    remove,
    clear,
    retry,
    ready: allReady(chips),
    ids: readyIds(chips),
  }
}
